/**
 * Navigation - Client-side SPA navigation system.
 *
 * Render-agnostic: delegates all rendering to the active RenderAdapter.
 * Uses ComponentResolver for client-side chain resolution (offline/PWA).
 */
import { Router } from '../hybrid/router.js';
import { ComponentResolver } from './component-resolver.js';

export class Navigation {
	stack = {};
	history = [];
	controllerHistory = [];
	current = null;
	currentChain = null;
	renderAdapter = null;
	router = null;

	constructor() {
		this.router = ssr.routes ? new Router(ssr.routes) : null;
		this.currentChain = ssr.chain || null;

		on(
			document,
			'click',
			'[href]:not(.fix):not([target=_blank])',
			(e) => this.catchHref(e),
		);

		window.onpopstate = async (popstate) => {
			const path = popstate.state.path;
			if (path) {
				if (this.stack[path] && this.stack[path].controller.$persistant) {
					await this.wakeUp(path);
				} else {
					loader.start();
					await this.loadController(path);
				}
			} else {
				await this.loadPage(path);
			}
		};
	}

	setRenderAdapter(adapter) {
		this.renderAdapter = adapter;
	}

	async loadPage(path, replace = false) {
		if (path.slice(0, 1) === '/') path = path.slice(1);
		loader.start();
		path = path.replace(baseUrl, '');
		path = path === '' ? '/' : path;
		l('loadPage', path);
		if (this.stack[path] && this.stack[path].controller.$persistant) {
			await this.wakeUp(path);
		} else {
			await this.loadController(path, replace);
		}
	}

	async reloadPage() {
		const currentPath = this.history[this.history.length - 1] || location.pathname.slice(1) || '/';
		return this.loadPage(currentPath);
	}

	async loadController(path, replace = false) {
		let response;

		try {
			if (this.router) {
				// Offline/PWA: resolve chain locally
				const route = this.router.process(location.origin + '/' + path);
				if (!route) {
					location.href = path;
					return;
				}

				const chain = await ComponentResolver.resolveChain(
					route.crate,
					route.component,
					{ patterns: route.patterns || {}, route }
				);

				const leaf = chain[chain.length - 1];

				// Component has no meaningful parts — treat as 404
				const noMeaningfulParts = !leaf?.component.view && !leaf?.component.provider && !leaf?.component.frontendHandler && !leaf?.component.controller;
				// Provider file exists but produced no data, and nothing else can display
				const emptyProvider = leaf && Object.keys(leaf.data).length === 0 && !leaf.component.view && !leaf.component.frontendHandler && !leaf.component.controller;

				if (noMeaningfulParts || emptyProvider) {
					response = { error: true, data: { error: true, title: '404', message: 'Not Found' } };
				} else {
					response = await ComponentResolver.prepareResponse(chain);
					response.css = ComponentResolver.getCssFromChain(chain);
				}
			} else {
				// Online: fetch from server, send current chain so server can skip unchanged wrapper views
				const headers = { systemInternal: true };
				if (this.currentChain) {
					headers.chain = JSON.stringify(
						this.currentChain.map(({ crate, component }) => ({ crate, component }))
					);
				}
				const request = await fetch(path, { headers });
				response = await request.json();
			}
		} catch (e) {
			console.error(`[Navigation Error] ${path}:`, e.message);
			response = { error: true, data: { error: true, title: 'Error', message: e.message } };
		}

		const { data, frontendHandler: handlerPath, css } = response;
		const isError = response.error || data?.error;

		let controller = {};
		if (handlerPath && !isError) {
			controller = await importFrontendHandler(handlerPath, data);
		}
		Object.assign(controller, data);

		if (css) {
			const cssLink = $('#mainCssBundle');
			if (cssLink) {
				cssLink.href = `/${css}.css`;
			}
		}

		// Compare wrapper chain (all levels except leaf) — if any wrapper differs, replace whole chain
		let chainChanged = false;
		if (response.chain && this.currentChain) {
			if (response.chain.length !== this.currentChain.length) {
				chainChanged = true;
			} else {
				// Compare all wrapper levels (everything except leaf)
				for (let i = 0; i < response.chain.length - 1; i++) {
					const a = response.chain[i];
					const b = this.currentChain[i];
					if (a.crate !== b.crate || a.component !== b.component) {
						chainChanged = true;
						break;
					}
				}
			}
		}

		if (!controller.$dontLoadContent) {
			this.current && this.current.$leave && (await this.current.$leave(controller));
			if (chainChanged) {
				await this.renderAdapter.replaceChain(response, controller);
			} else {
				await this.renderAdapter.update(response, controller);
			}
		}

		this.currentChain = response.chain;
		this.up(path, controller, replace);
		loader.end();
	}

	async wakeUp(path) {
		const controller = this.stack[path].controller;
		this.current && this.current.$leave && await this.current.$leave(controller);
		controller.$wakeUp && await controller.$wakeUp();
		controller.$awoken && controller.$awoken++;
		this.up(path, controller);
		loader.end();
	}

	up(path, controller, replace) {
		this.current = controller;
		this.history.push(path);
		this.controllerHistory.push(controller.name);
		let currentPath = location.pathname;
		if (currentPath.slice(0, 1) == '/') {
			currentPath = currentPath.slice(1);
		}
		if (currentPath == '') {
			currentPath = '/';
		}
		if (replace || currentPath == path || controller.$addToHistory === false) {
			window.history.replaceState({ path }, '');
		} else {
			window.history.pushState(
				{ path },
				'',
				baseUrl + (path.slice(0, 1) === '/' ? path : '/' + path),
			);
		}
		this.stack[path] = { path, controller };
		globalThis.controller = controller;

		controller.$classes ??= [];
		const className = controller.constructor.name.toLowerCase();
		if (!controller.$classes.includes(className)) {
			controller.$classes.push(className);
		}
		$('body').setAttribute('class', controller.$classes.join(' '));

		if (controller.$title) {
			$('head title').innerHTML = controller.$title;
		}
		controller.$afterLoad && controller.$afterLoad();
		observer.trigger('navigationUp', controller);
	}

	catchHref(e) {
		e.stopPropagation();
		e.preventDefault();
		let link = e.target.closest('[href]').getAttribute('href');
		l('catchHref', link);
		if (link[0] === '/') {
			link = link.slice(1);
		}
		this.loadPage(link);
	}
}

export default Navigation;
